<template>
  <div>
    <v-autocomplete
      v-model="model"
      :items="items"
      :loading="isLoading"
      :search-input.sync="search"
      color="black"
      item-color="black"
      item-text="Description"
      item-value="Description"
      :label="$t('components.requestInvoice.form.businessNamePlaceholder')"
      :placeholder="
        $t('components.requestInvoice.form.businessNamePlaceholder')
      "
      return-object
      no-filter
      :hint="hintText"
      persistent-hint
      :no-data-text="noDataText"
      class="py-2"
    >
      <template #prepend-item>
        <v-expand-transition>
          <div v-if="hasEntries" class="tp-text-label grey--text px-4 pt-2">
            {{ $t('components.searchForCompany.chooseCompany') }}
          </div>
        </v-expand-transition>
      </template>
      <template #no-data>
        <v-list-item class="search-for-company__no-data">
          <v-list-item-content>
            <v-list-item-title class="search-for-company__no-data-title">
              {{ noDataText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template #append-item>
        <v-divider color="#F5F5F5" class="mx-2" />
        <v-list-item @click="enterCompanyNameManually">
          <v-list-item-content>
            <v-list-item-title class="red--text text--lighten-1">
              {{ $t('components.searchForCompany.enterCompanyNameManually') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: 'SearchForCompany',
  props: {
    countryCode: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    descriptionLimit: 60,
    entries: [],
    isLoading: false,
    model: null,
    search: null,
    noDataText: '',
    showHint: true,
  }),
  computed: {
    hintText() {
      return this.showHint
        ? this.$t('components.searchForCompany.hintText')
        : undefined;
    },
    items() {
      return this.entries.map((entry) => {
        const Description =
          entry.title.length > this.descriptionLimit
            ? entry.title.slice(0, this.descriptionLimit) + '...'
            : entry.title;

        return Object.assign({}, entry, { Description });
      });
    },
    hasEntries() {
      return this.entries.length > 0;
    },
  },
  watch: {
    search: {
      handler: debounce(async function (val) {
        if (!val) {
          this.entries = [];
          this.isLoading = false;
          return;
        }

        if (val.length < 3) {
          this.entries = [];
          this.isLoading = false;
          return;
        }

        if (this.isLoading) return;

        try {
          this.isLoading = true;
          const response = await this.axios.post(
            `${process.env.VUE_APP_API_URL}company-search/by-name`,
            {
              country: this.countryCode,
              name: val,
            }
          );
          const data = response.data;

          if (data.length === 0) {
            this.noDataText = this.$t('components.searchForCompany.noDataText');
          }

          this.entries = data;
          this.isLoading = false;
        } catch (error) {
          this.entries = [];
          this.isLoading = false;

          const errors = {
            'Too many companies found':
              'components.searchForCompany.tooManyCompaniesFound',
          };

          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            const errorMessage =
              errors[error.response.data.message] || 'errors.defaultError';
            this.noDataText = this.$t(errorMessage);
          } else {
            console.error('Error fetching data:', error);
          }
          this.isLoading = false;
        }
      }, 1000),
      immediate: false,
    },
    model: {
      async handler(val) {
        if (!val) {
          return;
        }

        const fullData = await this.getMoreData(val.code);
        this.showHint = false;
        this.$emit('onCompanySelected', fullData);
      },
      immediate: false,
    },
  },
  mounted() {
    this.noDataText = this.$t('components.searchForCompany.hintText');
  },
  methods: {
    async getMoreData(code) {
      this.isLoading = true;
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}company-search/by-code`,
          {
            country: this.countryCode,
            code,
          }
        );
        const data = response.data;
        return data;
      } catch (error) {
        console.error('Error fetching more data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    enterCompanyNameManually() {
      this.$emit('onCompanyNameManually');
    },
  },
};
</script>

<style scoped>
.search-for-company__no-data-title {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
</style>
